import React, { useState } from "react";
import { motion } from "framer-motion";
import { Calendar, MapPin, Users } from "lucide-react";
import { fileUrl } from "@/lib/api";
import BookingDialog from "@/components/BookingDialog";

const categoryColors = {
  Concerto: "text-fuchsia-300 bg-fuchsia-500/10",
  "Visita Guidata": "text-sky-300 bg-sky-500/10",
  Escursione: "text-emerald-300 bg-emerald-500/10",
  Viaggio: "text-[#FFF200] bg-[#FFF200]/10",
  Matrimonio: "text-rose-300 bg-rose-500/10",
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const EventCard = ({ event, index = 0 }) => {
  const [open, setOpen] = useState(false);
  const catClass = categoryColors[event.category] || "text-[#FFF200] bg-[#FFF200]/10";

  const minReq = event.min_participants || 0;
  const paid = event.paid_participants || 0;
  const deadlinePassed =
    event.booking_deadline && new Date(event.booking_deadline) < new Date(new Date().toDateString());
  const closed = deadlinePassed || event.status === "cancelled";
  const pct = minReq > 0 ? Math.min(100, Math.round((paid / minReq) * 100)) : 0;

  return (
    <>
      <motion.article
        data-testid={`event-card-${event.id}`}
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: (index % 3) * 0.1 }}
        className="group flex flex-col bg-[#121212] border border-[#27272A] rounded-2xl overflow-hidden hover:-translate-y-1 hover:border-[#FFF200]/50 hover:shadow-[0_8px_30px_rgba(255,242,0,0.08)] transition-all duration-300"
      >
        <div className="relative aspect-[16/10] overflow-hidden bg-black">
          {event.image_url ? (
            <img
              src={fileUrl(event.image_url)}
              alt={event.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/20 font-script text-2xl">
              Cardillo Viaggi
            </div>
          )}
          <span
            className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${catClass}`}
          >
            {event.category}
          </span>
        </div>

        <div className="flex flex-col flex-1 p-6">
          <div className="flex items-center gap-4 text-xs text-white/50 mb-3">
            <span className="flex items-center gap-1.5">
              <Calendar size={13} className="text-[#FFF200]" /> {formatDate(event.event_date)}
            </span>
            {event.location && (
              <span className="flex items-center gap-1.5 truncate">
                <MapPin size={13} className="text-[#FFF200]" /> {event.location}
              </span>
            )}
          </div>

          <h3 className="text-xl font-display font-semibold text-white mb-2">{event.title}</h3>
          <p className="text-white/60 text-sm leading-relaxed line-clamp-3 flex-1">
            {event.description}
          </p>

          {minReq > 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-white/50 flex items-center gap-1">
                  <Users size={12} className="text-[#FFF200]" /> {paid}/{minReq} persone
                </span>
                <span className={paid >= minReq ? "text-emerald-400" : "text-amber-400"}>
                  {paid >= minReq ? "Confermato" : "In raccolta"}
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    paid >= minReq ? "bg-emerald-400" : "bg-[#FFF200]"
                  }`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              {event.booking_deadline && (
                <p className="text-white/40 text-xs mt-1.5">
                  Prenotazioni entro il{" "}
                  {new Date(event.booking_deadline).toLocaleDateString("it-IT", {
                    day: "2-digit",
                    month: "long",
                  })}
                </p>
              )}
            </div>
          )}

          <div className="flex items-center justify-between mt-5 pt-5 border-t border-white/10">
            <div>
              <span className="text-2xl font-display font-bold text-[#FFF200]">
                €{Number(event.price).toFixed(2)}
              </span>
              <span className="text-white/40 text-xs block">a persona</span>
            </div>
            <button
              data-testid={`book-btn-${event.id}`}
              disabled={closed}
              onClick={() => setOpen(true)}
              className="bg-[#FFF200] text-black font-semibold px-5 py-2.5 rounded-full text-sm hover:bg-[#FFF200]/90 transition-colors disabled:bg-white/10 disabled:text-white/40 disabled:cursor-not-allowed"
            >
              {closed ? "Chiuse" : "Prenota"}
            </button>
          </div>
        </div>
      </motion.article>

      <BookingDialog event={event} open={open} onOpenChange={setOpen} />
    </>
  );
};

export default EventCard;
